<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <section class="page-section q-mb-lg">
      <q-form ref="form" class="full-width">
        <div class="row q-col-gutter-md">
          <div class="col-6">
            <q-input
              v-model="formData.id"
              :rules="[(val) => !!val || 'Field is required']"
              outlined
              dense
              :readonly="isEditMode"
              label="ID *"
              class="bg-white full-width"
              @update:model-value="onFormChange"
            />
          </div>
        </div>
      </q-form>
    </section>

    <section class="page-section">
      <div class="flex items-center q-mb-md">
        <span>Dark theme</span>
        <q-toggle false-value="vs-dark" class="q-mx-sm" true-value="vs" v-model="theme" />
        <span>Light theme</span>
      </div>

      <div class="row q-col-gutter-md">
        <div class="col-12">
          <q-card flat bordered class="full-height">
            <q-card-section class="q-pb-none">
              <div class="text-subtitle1 text-weight-bold">Schema</div>
            </q-card-section>
            <q-card-section>
              <JsonEditor :theme="theme" v-model="formData.schema" height="500px" @update:model-value="onFormChange" />
            </q-card-section>
          </q-card>
        </div>
      </div>
    </section>

    <section v-if="validationErrors.length" class="page-section q-mt-lg">
      <div class="text-subtitle1 text-weight-bold text-negative q-mb-sm">Validation Errors:</div>
      <ul class="validation-errors">
        <li v-for="(error, index) in validationErrors" :key="index" class="text-negative">
          {{ error }}
        </li>
      </ul>
    </section>

    <div class="buttons row q-mt-lg">
      <div class="col-12 flex q-gutter-sm">
        <q-btn
          v-if="!isValidated"
          color="primary"
          label="Validate"
          no-caps
          @click="validateForm"
          :loading="validating"
          :disabled="!isDirty || disableEditMessage"
        >
          <q-tooltip style="z-index: 99999" v-if="disableEditMessage">{{ disableEditMessage }}</q-tooltip>
        </q-btn>
        <q-btn
          v-else
          color="positive"
          :label="isEditMode ? 'Update' : 'Create'"
          no-caps
          @click="sendData"
          :loading="saving"
        />
        <q-btn flat no-caps label="Cancel" @click="$router.push('/service-schemas')" />
      </div>
    </div>
  </q-page>
</template>

<script>
import { isEqual } from 'lodash';
import { validateWorkFlow, createWorkFlow } from '../registrations/api/api';
import { getServiceSchema } from './api/api';
import JsonEditor from './components/JsonEditor.vue';

export default {
  name: 'ServiceSchemaPage',
  components: {
    JsonEditor,
  },
  data() {
    return {
      theme: 'vs-dark',
      formData: {
        id: undefined,
        schema: '{}',
      },
      originalFormData: null,
      isValidated: false,
      validating: false,
      saving: false,
      validationErrors: [],
      disableEditMessage: undefined,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    isEditMode() {
      return !!this.id && this.id !== 'new';
    },
    isDirty() {
      if (!this.originalFormData) return false;
      return !isEqual(this.formData, this.originalFormData);
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.loadSchema();
    } else {
      this.originalFormData = JSON.parse(JSON.stringify(this.formData));
    }
  },
  methods: {
    onFormChange() {
      this.isValidated = false;
      this.validationErrors = [];
    },
    validateJsonFields() {
      const errors = [];

      try {
        JSON.parse(this.formData.schema);
      } catch (e) {
        errors.push('Schema: Invalid JSON syntax');
      }

      return errors;
    },
    buildPayload() {
      const payload = {
        schema: JSON.parse(this.formData.schema),
      };

      if (this.isEditMode) {
        payload.uuid = this.formData.uuid;
      } else {
        payload.id = this.formData.id;
      }

      return payload;
    },
    async validateForm() {
      const isFormValid = await this.$refs.form.validate();
      if (!isFormValid) {
        this.$notify('negative', 'Error', 'Please fill in all required fields');
        return;
      }

      const jsonErrors = this.validateJsonFields();
      if (jsonErrors.length) {
        this.validationErrors = jsonErrors;
        this.$notify('negative', 'Error', 'Invalid JSON in schema field');
        return;
      }

      this.validating = true;
      this.validationErrors = [];

      try {
        const payload = this.buildPayload();
        const response = await validateWorkFlow({
          inputVariables: payload,
          workflowId: this.isEditMode ? 'engprt.update-service-schema' : 'engprt.create-service-schema',
        });

        if (response.valid || response.success || !response.errors?.length) {
          this.isValidated = true;
          this.$notify('positive', 'Success', 'Validation successful. You can now save the schema.');
        } else {
          this.validationErrors = response.errors || [response.message || 'Validation failed'];
          this.$notify('negative', 'Validation Failed', 'Please fix the errors and try again');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Validation failed';
        this.validationErrors = Array.isArray(errorMessage) ? errorMessage : [errorMessage];
        this.$notify('negative', 'Error', errorMessage);
      } finally {
        this.validating = false;
      }
    },
    async sendData() {
      if (!this.isValidated) {
        this.$notify('warning', 'Warning', 'Please validate the form first');
        return;
      }

      this.saving = true;

      try {
        const payload = this.buildPayload();
        await createWorkFlow({
          inputVariables: payload,
          workflowId: this.isEditMode ? 'engprt.update-service-schema' : 'engprt.create-service-schema',
        });

        this.$notify('positive', 'Success', `Service Schema ${this.isEditMode ? 'updated' : 'created'} successfully`);
        this.$router.push('/service-schemas');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save schema';
        this.$notify('negative', 'Error', errorMessage);
      } finally {
        this.saving = false;
      }
    },
    async loadSchema() {
      this.$showLoading();
      try {
        const response = await getServiceSchema(this.id);
        this.formData = {
          id: response.id,
          uuid: response.uuid,
          schema: JSON.stringify(response.schema, null, 2),
        };
        this.originalFormData = JSON.parse(JSON.stringify(this.formData));

        const { uuid, schema } = response;
        await validateWorkFlow({
          workflowId: 'engprt.update-service-schema',
          inputVariables: {
            uuid,
            schema,
          },
        }).catch((res) => {
          this.disableEditMessage = res.message;
        });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.$hideLoading();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-section {
  background-color: white;
  border-radius: 12px;
  padding: 16px;
}

.validation-errors {
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 4px;
  }
}
</style>
